/**
 * ONE palette. One place that turns a platform answer into the tiles an author sees.
 *
 * WHY THIS FILE EXISTS. There were two, and that is the whole defect. The unit page passed
 * Studio's `component_templates` straight to `PalettePicker`, which flattened and grouped it
 * one way; the library page had its own `paletteGroups()` that flattened and grouped
 * `block_types/` another way. Two implementations of one list is why an author saw two
 * different lists: the course palette collapsed an 8-template family to a single tile while
 * the library happened to build one-type-one-template families, so everything showed. Same
 * component, different data shaping. Fixing both to draw 28 tiles and leaving the shaping
 * forked would only mean the next change breaks one surface and not the other.
 *
 * So: the two ADAPTERS below are per-surface, because the two endpoints genuinely differ,
 * and everything after them — the capability filter, what hides behind the disclosure, the
 * search ranking, the grouping decision, the count on the toggle — happens exactly once,
 * here, for both.
 *
 * WHAT IS SHARED, AND WHY EACH PART IS A PROPERTY OF THE SHARED PATH RATHER THAN A CALLER'S
 * HABIT:
 *
 *   * EVERY TEMPLATE IS A TILE. `flattenPalette` walks every template in every family. The
 *     measured defect this replaces: on `SUST102` the platform returned 10 groups and 28
 *     templates and the palette drew 10 tiles, because only each family's first template
 *     (`primary`) was rendered. Poll, Survey, Word cloud, LTI and PDF are enabled on every
 *     course on this deployment by `DEFAULT_ADVANCED_MODULES`, so nothing was restricting
 *     them — the component was hiding them.
 *
 *   * A CONTAINER TYPE IS EXCLUDED ON `has_children`, NEVER ON A NAME. `conditional` is not
 *     mentioned anywhere in this file. A library refuses ANY block type that holds children
 *     ("has children, so it is not supported in content libraries", HTTP 400), which today
 *     happens to be `conditional` alone on this install and will be a different set after
 *     the next XBlock is installed. Asking the platform which types are containers is a
 *     question that stays correct; a name list is right until it is silently wrong.
 *
 *   * AND IT FAILS OPEN. If the capability answer is missing — the call failed, or has not
 *     landed yet — nothing is excluded. Filtering on an empty answer would empty the
 *     palette and tell the author this library accepts no block types at all, which is a
 *     lie about a failed request. The fallback is the behaviour that existed before the
 *     filter: offer everything, and let the platform refuse the one that cannot work with
 *     its own readable sentence, which the palette already shows without closing.
 *
 *   * THE EXCLUSION IS A PROPERTY OF THE CONTAINER, NOT OF THE SHAPING, which is why it is
 *     keyed off `surface` here instead of being a flag each caller remembers to pass. A
 *     library cannot store a container; a course unit can, and does — `library_content` and
 *     `itembank` both hold children and are both legitimately creatable in a unit. Applying
 *     the library's rule to a course would delete working types from the course palette,
 *     which is the same class of mistake as the name list.
 *
 *   * A DEPRECATED TYPE IS DEMOTED, NOT REMOVED, and now at one call site instead of two.
 *     `library_content` stays creatable for an install that still needs one; it just has no
 *     business sitting in the first nine tiles. Whether it should be offered at all remains
 *     the owner's call — this file is where that call would be made.
 *
 *   * THE DISCLOSURE COUNTS TEMPLATES. It used to count families, so it read "Show 1
 *     advanced type" in front of eight. A label that understates what it hides eightfold is
 *     worse than no label.
 *
 * WHAT REMAINS LEGITIMATELY DIFFERENT, said plainly so nobody has to re-derive it: the DATA
 * SOURCE. A unit's creatable types come from `container_handler`'s `component_templates`,
 * which is Studio's own vocabulary — families of templates, where one family offers several
 * starting points (a `problem` with different boilerplate). A library's come from
 * `block_types/`, a flat list of types with one way to create each and no boilerplate at
 * all. Two endpoints, two shapes, two different sets of rules about what is creatable. The
 * adapters exist for exactly that and do nothing else; they invent no template a payload did
 * not contain.
 *
 * AND THERE IS NO UPSTREAM PICKER TO DEFER TO. Where upstream ships a UI we use it — the
 * component EDITOR in this app is Studio's own `studio_view` in an iframe, which is why
 * fixing the advertised scheme fixed the editor's blocked stylesheets rather than any code
 * here. For the picker there is nothing to reuse: upstream deleted `problem`'s `studio_view`
 * in this release and no authoring MFE is deployed in this namespace, so no upstream
 * component picker exists to embed. Upstream's editors where they exist, our picker because
 * theirs is gone.
 */

import type { BlockTypeCapability, LibraryBlockType } from './library';
import {
  DEPRECATED_CATEGORIES,
  type ComponentTemplate,
  type ComponentTemplateGroup,
} from './unit';

/* ------------------------------------------------------------------- the sources ---- */

/**
 * Where a palette's types come from, and therefore what rules apply to them.
 *
 * A discriminated union rather than two exported functions, so a caller cannot pick the
 * shaping of one surface and the filtering of the other — which is the state this file
 * exists to make unrepresentable.
 */
export type PaletteSource =
  | { surface: 'course'; groups: ComponentTemplateGroup[] }
  | { surface: 'library'; types: LibraryBlockType[] };

/**
 * The types an author reaches for, in the order they reach for them.
 *
 * Applies to the LIBRARY adapter only, because `block_types/` has no order worth keeping —
 * it comes back alphabetically, which put "Conditional" first, and with the old `<select>`
 * that made the DEFAULT choice a guaranteed 400. A course's answer already arrives with each
 * family's default first, and that order is Studio's own opinion about the same question, so
 * it is preserved rather than re-sorted.
 *
 * Deliberately a PREFERENCE and not a filter: everything else keeps its place after these,
 * behind the disclosure. Hiding a type we do not recognise would silently remove a working
 * XBlock someone installed on purpose.
 */
const PREFERRED_TYPES = ['html', 'problem', 'video', 'drag-and-drop-v2', 'openassessment'];

/* ------------------------------------------------------------------ the entries ---- */

/** A template plus the family it came from, so search results stay attributable. */
export interface PaletteEntry {
  group: ComponentTemplateGroup;
  template: ComponentTemplate;
  /** True for the family's default — the one an author usually wants. */
  primary: boolean;
}

/** One block of tiles, with a heading only where a heading says something. */
export interface PaletteSection {
  key: string;
  title: string | null;
  /** True in flat search results, where a tile has to carry its own family. */
  showFamily: boolean;
  entries: PaletteEntry[];
}

/**
 * Which block types hold children, as a set, from an answer that may not have arrived.
 *
 * `undefined` in means an empty set out, and that is the fail-open decision in one line:
 * an unanswered question excludes nothing.
 */
function childHolders(capabilities: BlockTypeCapability[] | undefined): Set<string> {
  return new Set(
    (capabilities ?? []).filter((row) => row.has_children).map((row) => row.block_type),
  );
}

/** `block_types/`'s flat list as the group shape, one family per type. */
function fromLibraryTypes(types: LibraryBlockType[]): ComponentTemplateGroup[] {
  const rank = (type: string) => {
    const at = PREFERRED_TYPES.indexOf(type);
    return at === -1 ? PREFERRED_TYPES.length : at;
  };
  const ordered = [...types].sort((a, b) => rank(a.block_type) - rank(b.block_type));

  return ordered.map((type) => ({
    // The one field doing real work. The palette hides the 'advanced' family behind the
    // disclosure, so naming a type there is how the preference list above becomes "the five
    // an author wants, then everything else on request".
    type: PREFERRED_TYPES.includes(type.block_type) ? type.block_type : 'advanced',
    display_name: type.display_name,
    templates: [
      {
        display_name: type.display_name,
        category: type.block_type,
        // No boilerplate in a library: `blocks/` takes a type and nothing else.
        boilerplate_name: null,
        tab: '',
        support_level: true,
        hinted: false,
      },
    ],
    support_legend: { show_legend: false, allow_unsupported_xblocks: false },
  }));
}

/**
 * A platform answer as the grouped tile list, for either surface.
 *
 * The single entry point. Both pages call this and nothing else, so a change to what the
 * palette offers is one edit that lands on both, and a divergence between them is a test
 * failure rather than a thing an author notices.
 */
export function paletteGroups(
  source: PaletteSource,
  capabilities?: BlockTypeCapability[],
): ComponentTemplateGroup[] {
  const groups =
    source.surface === 'library' ? fromLibraryTypes(source.types) : source.groups;

  // A course unit stores containers perfectly well, so the library's rule does not travel.
  if (source.surface === 'course') return groups;

  const holdsChildren = childHolders(capabilities);
  if (holdsChildren.size === 0) return groups;

  return groups
    .map((group) => ({
      ...group,
      templates: group.templates.filter((t) => !holdsChildren.has(t.category)),
    }))
    .filter((group) => group.templates.length > 0);
}

/** Every template in every family, in the order the platform gave them. */
export function flattenPalette(groups: ComponentTemplateGroup[]): PaletteEntry[] {
  return groups.flatMap((group) =>
    group.templates.map((template, index) => ({ group, template, primary: index === 0 })),
  );
}

/**
 * The two kinds of entry that live behind the disclosure, and only these two.
 *
 * The Advanced family, because that is what the toggle has always said it hides. And a
 * deprecated type, because upstream asks that nothing new be built on it.
 */
export function behindDisclosure(entry: PaletteEntry): boolean {
  return entry.group.type === 'advanced' || DEPRECATED_CATEGORIES.has(entry.template.category);
}

/** How many templates the disclosure is hiding. TEMPLATES, not families. */
export function disclosureCount(groups: ComponentTemplateGroup[]): number {
  return flattenPalette(groups).filter(behindDisclosure).length;
}

/**
 * Score a match so the ordering is useful rather than alphabetical.
 *
 * A prefix match beats a match in the middle, and a family's default beats its boilerplates:
 * typing "pro" should offer Problem before "Zooming Image Tool" even though both contain the
 * letters. Returns -1 for no match.
 */
export function scoreEntry(entry: PaletteEntry, query: string): number {
  if (!query) return entry.primary ? 0 : 1;
  const name = entry.template.display_name.toLowerCase();
  const family = entry.group.display_name.toLowerCase();
  const at = name.indexOf(query);
  const familyAt = family.indexOf(query);
  if (at < 0 && familyAt < 0) return -1;
  const base = at === 0 ? 0 : at > 0 ? 10 : familyAt === 0 ? 20 : 30;
  return base + (entry.primary ? 0 : 1);
}

/**
 * The tiles to draw, grouped, for a query and a disclosure state.
 *
 * Shared rather than per-page, and the grouping decision is read off the DATA rather than
 * passed in by whoever is calling: a family that really offers several starting points earns
 * a heading, and a list of one-template families would only repeat every tile's own name.
 * That is why a course list groups and a library list does not, without either page knowing
 * it is the one that groups.
 */
export function paletteSections(
  groups: ComponentTemplateGroup[],
  { query, advanced }: { query: string; advanced: boolean },
): PaletteSection[] {
  const needle = query.trim().toLowerCase();
  const scored = flattenPalette(groups)
    .map((entry) => ({ entry, rank: scoreEntry(entry, needle) }))
    .filter((row) => row.rank >= 0);

  // Searching flattens the palette into one ranked list, because groups get in the way once
  // you know what you are looking for — and search reaches behind the disclosure, which is
  // the whole reason a hidden type was findable but not discoverable.
  if (needle) {
    return [
      {
        key: 'results',
        title: null,
        showFamily: true,
        entries: scored.sort((a, b) => a.rank - b.rank).map((row) => row.entry),
      },
    ];
  }

  const shown = scored
    .map((row) => row.entry)
    .filter((entry) => advanced || !behindDisclosure(entry));

  const grouped = groups.some((group) => group.templates.length > 1);
  if (!grouped) return [{ key: 'all', title: null, showFamily: false, entries: shown }];

  // The server's order, preserved: it puts each family's default first, which is the order
  // `scoreEntry` already agrees with.
  const order: string[] = [];
  const byGroup = new Map<string, PaletteEntry[]>();
  for (const entry of shown) {
    const key = entry.group.type;
    const bucket = byGroup.get(key);
    if (bucket) bucket.push(entry);
    else {
      byGroup.set(key, [entry]);
      order.push(key);
    }
  }

  return order.map((key) => {
    const bucket = byGroup.get(key) ?? [];
    const family = bucket[0]?.group.display_name ?? null;
    // A heading only where it says something the tiles do not. "VIDEO" over a single tile
    // called "Video" is one word twice, and a course's list has five such families — enough
    // of them to make the whole dialog read like a form.
    const needed = bucket.length > 1 || (bucket[0] && bucket[0].template.display_name !== family);
    return { key, title: needed ? family : null, showFamily: false, entries: bucket };
  });
}
